import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTumProperties } from "@/hooks/useTumProperties";
import { useTumCustomers } from "@/hooks/useTumCustomers";

const propertySchema = z.object({
  property_name: z.string().min(2, "Property name must be at least 2 characters"),
  property_code: z.string().optional(),
  property_type: z.string().optional(),
  customer_id: z.string().uuid().optional(),
  address: z.any().optional(),
  total_units: z.coerce.number().int().min(1).default(1),
  status: z.string().optional(),
});

type PropertyFormData = z.infer<typeof propertySchema>;

interface TumPropertyFormProps {
  property?: any;
  onSuccess?: () => void;
}

export const TumPropertyForm = ({ property, onSuccess }: TumPropertyFormProps) => {
  const { createProperty, updateProperty, isCreating, isUpdating } = useTumProperties();
  const { customers } = useTumCustomers();

  const form = useForm<PropertyFormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: property ? {
      property_name: property.property_name,
      property_code: property.property_code || '',
      property_type: property.property_type || 'residential',
      customer_id: property.customer_id || '',
      address: property.address || {},
      total_units: property.total_units || 1,
      status: property.status || 'active',
    } : {
      property_type: 'residential',
      status: 'active',
      total_units: 1,
      address: {},
    },
  });

  const onSubmit = (data: PropertyFormData) => {
    if (property) {
      updateProperty({ id: property.id, ...data as any });
      onSuccess?.();
    } else {
      createProperty(data as any);
      form.reset();
      onSuccess?.();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="property_name"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Property Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Sunset Apartments" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="property_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Code</FormLabel>
                <FormControl>
                  <Input placeholder="PROP-001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="property_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Type *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="mixed">Mixed Use</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="customer_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Portfolio Owner</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="total_units"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Total Units *</FormLabel>
                <FormControl>
                  <Input type="number" min="1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating || isUpdating}>
            {property ? 'Update' : 'Create'} Property
          </Button>
        </div>
      </form>
    </Form>
  );
};
